/// <reference lib="dom" />
/**
 * Shared font loading helpers used by both the build (vite.config.ts) and
 * runtime CMS theme overrides (PublicLayout). Keeping them here means a
 * site's configured font and an admin-picked font in /admin/theme load
 * through identical logic — no drift between build-time and CMS paths.
 *
 * The triple-slash DOM reference is here because vite.config.ts imports
 * this module, which pulls it into the node-only tsconfig project that
 * doesn't include the DOM lib by default.
 */

const SYSTEM_FONTS = new Set<string>([
  'system-ui',
  'sans-serif',
  'serif',
  'monospace',
  'ui-sans-serif',
  'ui-serif',
  'ui-monospace',
  'Georgia',
  'Arial',
  'Helvetica',
  'Times New Roman',
  'Courier New',
  'Verdana',
  'Tahoma',
  'Trebuchet MS',
]);

export function isSystemFont(name: string): boolean {
  return SYSTEM_FONTS.has(name.trim());
}

export function buildGoogleFontsHref(names: string[]): string | null {
  const families = Array.from(
    new Set(names.map((n) => n.trim()).filter((n) => n && !isSystemFont(n)))
  );
  if (families.length === 0) return null;
  const params = families
    .map((n) => `family=${n.replace(/ /g, '+')}:wght@400;500;600;700`)
    .join('&');
  return `https://fonts.googleapis.com/css2?${params}&display=swap`;
}

const loadedFonts = new Set<string>();

export function loadGoogleFont(name: string): void {
  const trimmed = name.trim();
  if (!trimmed || isSystemFont(trimmed) || loadedFonts.has(trimmed)) return;
  const href = buildGoogleFontsHref([trimmed]);
  if (!href) return;
  loadedFonts.add(trimmed);
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);
}
